declare const routes: {
  predictive_search_url: string;
};
export class PredictiveSearch extends HTMLElement {
  private cachedResults: Record<string, string> = {};
  private input: HTMLInputElement | null;
  private predictiveSearchResults: HTMLElement | null;
  private isOpen: boolean;
  private statusElement: HTMLElement;
  private loadingText: string | null = null;
  private resultsMaxHeight: number = 0;
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector<HTMLElement>(
      "[data-predictive-search]",
    );
    this.statusElement = this.querySelector(
      "[data-predictive-search-status]",
    ) as HTMLElement;
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners(): void {
    const form = this.querySelector<HTMLFormElement>("form.search");
    form?.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input?.addEventListener(
      "input",
      _psDebounce(() => {
        this.onChange();
      }, 300).bind(this),
    );
    this.input?.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery(): string {
    return this.input?.value.trim() ?? "";
  }

  onChange(): void {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event: SubmitEvent): void {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus(): void {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(): void {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event: KeyboardEvent): void {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event: KeyboardEvent): void {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction: "up" | "down"): void {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector<HTMLLIElement>("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = (selectedElement.nextElementSibling ||
        allElements[0]) as HTMLLIElement;
    } else if (moveUp) {
      activeElement = (selectedElement?.previousElementSibling ||
        allElements[allElements.length - 1]) as HTMLLIElement;
    }
    if (!activeElement) return;
    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", "true");
    if (selectedElement) selectedElement.setAttribute("aria-selected", "false");

    this.setLiveRegionText(activeElement.textContent);
    this.input?.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption(): void {
    const selectedProduct = this.querySelector<HTMLButtonElement>(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm: string): void {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product&${encodeURIComponent("resources[limit]")}=4&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status.toString());
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup =
          new DOMParser()
            .parseFromString(text, "text/html")
            .querySelector("#shopify-section-predictive-search")?.innerHTML ??
          "";
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState(): void {
    this.statusElement =
      this.statusElement ||
      this.querySelector<HTMLElement>(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", "true");
  }

  setLiveRegionText(statusText: string | null) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup: string): void {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", "true");

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults(): void {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent ?? null,
    );
  }

  getResultsMaxHeight(): number {
    this.resultsMaxHeight =
      window.innerHeight -
      (document
        .getElementById("shopify-section-header")
        ?.getBoundingClientRect().bottom ?? 0);
    return this.resultsMaxHeight;
  }

  open(): void {
    if (!this.predictiveSearchResults) return;
    this.predictiveSearchResults.style.maxHeight = `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`;
    this.setAttribute("open", "true");
    this.input?.setAttribute("aria-expanded", "true");
    this.isOpen = true;
  }

  close(clearSearchTerm = false): void {
    if (clearSearchTerm) {
      if (!this.input) return;
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", "false");
    if (!this.input) return;
    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", "false");
    this.resultsMaxHeight = 0;
    this.predictiveSearchResults?.removeAttribute("style");

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);

function _psDebounce(func: (...args: unknown[]) => void, timeout = 300) {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}
